import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { GoalsState, Goal, Contribution, GoalStatus, GoalType } from '@/types'
import { GOAL_COLORS } from '@/types/goals.types'

const initialState: GoalsState = {
  goals: [],
  totalSaved: 0,
  activeGoalsCount: 0,
  completedGoalsCount: 0,
}

// Helper to recalculate derived state
function recalculateTotals(state: GoalsState) {
  state.totalSaved = state.goals.reduce((sum, goal) => sum + goal.currentAmount, 0)
  state.activeGoalsCount = state.goals.filter(g => g.status === 'active').length
  state.completedGoalsCount = state.goals.filter(g => g.status === 'completed').length
}

// Helper to generate a unique ID
function generateId(): string {
  return `goal_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

// Helper to get next available color
function getNextColor(goals: Goal[]): string {
  const usedColors = goals.map(g => g.color)
  const availableColor = GOAL_COLORS.find(c => !usedColors.includes(c))
  return availableColor || GOAL_COLORS[goals.length % GOAL_COLORS.length]
}

const goalsSlice = createSlice({
  name: 'goals',
  initialState,
  reducers: {
    addGoal: (state, action: PayloadAction<{
      name: string
      type: GoalType
      targetAmount: number
      deadline: string | null
      priority?: number
      color?: string
      notes?: string
      linkedAccountId?: string | null
      linkedAccountType?: 'investment' | 'debt' | null
    }>) => {
      const { name, type, targetAmount, deadline, priority, color, notes, linkedAccountId, linkedAccountType } = action.payload
      
      const newGoal: Goal = {
        id: generateId(),
        name,
        type,
        targetAmount,
        currentAmount: 0,
        deadline,
        createdAt: new Date().toISOString(),
        completedAt: null,
        status: 'active',
        priority: priority ?? state.goals.length + 1,
        color: color ?? getNextColor(state.goals),
        linkedAccountId: linkedAccountId ?? null,
        linkedAccountType: linkedAccountType ?? null,
        notes: notes ?? '',
        contributions: [],
      }
      
      state.goals.push(newGoal)
      recalculateTotals(state)
    },
    
    updateGoal: (state, action: PayloadAction<{
      id: string
      updates: Partial<Omit<Goal, 'id' | 'createdAt' | 'contributions'>>
    }>) => {
      const { id, updates } = action.payload
      const goalIndex = state.goals.findIndex(g => g.id === id)
      
      if (goalIndex !== -1) {
        state.goals[goalIndex] = { ...state.goals[goalIndex], ...updates }
        recalculateTotals(state)
      }
    },
    
    deleteGoal: (state, action: PayloadAction<string>) => {
      state.goals = state.goals.filter(g => g.id !== action.payload)
      recalculateTotals(state)
    },
    
    addContribution: (state, action: PayloadAction<{
      goalId: string
      amount: number
      note?: string
    }>) => {
      const { goalId, amount, note } = action.payload
      const goal = state.goals.find(g => g.id === goalId)
      
      if (goal) {
        const contribution: Contribution = {
          id: `contrib_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          amount,
          date: new Date().toISOString(),
          note: note ?? '',
        }
        
        goal.contributions.push(contribution)
        goal.currentAmount += amount
        
        // Check if goal is now complete
        if (goal.currentAmount >= goal.targetAmount && goal.status === 'active') {
          goal.status = 'completed'
          goal.completedAt = new Date().toISOString()
        }
        
        recalculateTotals(state)
      }
    },
    
    removeContribution: (state, action: PayloadAction<{
      goalId: string
      contributionId: string
    }>) => {
      const { goalId, contributionId } = action.payload
      const goal = state.goals.find(g => g.id === goalId)
      
      if (goal) {
        const contribution = goal.contributions.find(c => c.id === contributionId)
        if (contribution) {
          goal.currentAmount -= contribution.amount
          goal.contributions = goal.contributions.filter(c => c.id !== contributionId)
          
          // Revert completed status if needed
          if (goal.currentAmount < goal.targetAmount && goal.status === 'completed') {
            goal.status = 'active'
            goal.completedAt = null
          }
          
          recalculateTotals(state)
        }
      }
    },
    
    setGoalStatus: (state, action: PayloadAction<{
      goalId: string
      status: GoalStatus
    }>) => {
      const { goalId, status } = action.payload
      const goal = state.goals.find(g => g.id === goalId)
      
      if (goal) {
        goal.status = status
        if (status === 'completed') {
          goal.completedAt = new Date().toISOString()
        } else {
          goal.completedAt = null
        }
        recalculateTotals(state)
      }
    },
    
    reorderGoals: (state, action: PayloadAction<{ goalId: string; newPriority: number }>) => {
      const { goalId, newPriority } = action.payload
      const goal = state.goals.find(g => g.id === goalId)
      
      if (goal) {
        const oldPriority = goal.priority
        
        state.goals.forEach(g => {
          if (g.id === goalId) {
            g.priority = newPriority
          } else if (oldPriority < newPriority) {
            // Moving down: decrease priority of goals in between
            if (g.priority > oldPriority && g.priority <= newPriority) {
              g.priority -= 1
            }
          } else {
            // Moving up: increase priority of goals in between
            if (g.priority >= newPriority && g.priority < oldPriority) {
              g.priority += 1
            }
          }
        })
      }
    },
    
    updateLinkedGoalAmount: (state, action: PayloadAction<{
      goalId: string
      amount: number
    }>) => {
      const { goalId, amount } = action.payload
      const goal = state.goals.find(g => g.id === goalId)
      
      if (goal && goal.linkedAccountId) {
        goal.currentAmount = amount
        
        // Check if goal is now complete
        if (goal.currentAmount >= goal.targetAmount && goal.status === 'active') {
          goal.status = 'completed'
          goal.completedAt = new Date().toISOString()
        }
        
        recalculateTotals(state)
      }
    },
    
    completeGoal: (state, action: PayloadAction<string>) => {
      const goal = state.goals.find(g => g.id === action.payload)
      if (goal) {
        goal.status = 'completed'
        goal.completedAt = new Date().toISOString()
        recalculateTotals(state)
      }
    },
    
    setGoalsData: (state, action: PayloadAction<Partial<GoalsState>>) => {
      if (action.payload.goals !== undefined) {
        state.goals = action.payload.goals
      }
      recalculateTotals(state)
    },
  },
})

export const {
  addGoal,
  updateGoal,
  deleteGoal,
  addContribution,
  removeContribution,
  setGoalStatus,
  reorderGoals,
  updateLinkedGoalAmount,
  completeGoal,
  setGoalsData,
} = goalsSlice.actions

export default goalsSlice.reducer
